import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ScrollText, ShieldAlert, Info, UserX } from 'lucide-react';

const SECTIONS = [
  {
    icon: Info,
    color: '#00f0ff',
    title: '1. Informational use only',
    body:
      'This website is provided for general informational and personal-portfolio purposes only. The site administrator and owner make no representation or warranty as to the accuracy, completeness, currency, or reliability of any content, opinion, link, RSS-ingested article, or third-party material displayed on the site. The site admin and owner do not take any accountability for any information being shown to the user. You agree that any reliance you place on such information is strictly at your own risk.',
  },
  {
    icon: ShieldAlert,
    color: '#ff9e00',
    title: '2. Admin reserves full right to revoke access',
    body:
      'The site administrator reserves the absolute, sole, and unilateral right to disable, restrict, suspend, or revoke any user account, registration request, comment, reply, or any other form of access to the website at any time, for any reason or no reason, and WITHOUT prior notice to the user. No appeal mechanism is guaranteed. By registering you acknowledge and accept this.',
  },
  {
    icon: UserX,
    color: '#ef4444',
    title: '3. User accounts may be deleted without notice',
    body:
      'The site administrator may permanently delete your user account, your guestbook entries, your replies, and any associated metadata at any time WITHOUT prior notice. No data-recovery service is offered for deleted accounts. Approval of a registration request does not guarantee continued access. Users have no contractual right of persistence on this platform.',
  },
  {
    icon: ScrollText,
    color: '#9d4edd',
    title: '4. User-generated content',
    body:
      'Comments and replies you post are visible to other visitors. You retain ownership of the content you submit, but grant the site a perpetual, royalty-free license to host, display, and moderate it. Do not post content that is unlawful, defamatory, infringing, or otherwise objectionable. Posts deemed inappropriate may be deleted immediately without warning by the admin.',
  },
  {
    icon: ShieldAlert,
    color: '#39ff14',
    title: '5. Limitation of liability',
    body:
      'In no event shall the site administrator, owner, hosting providers, or related parties be liable for any direct, indirect, incidental, special, consequential, or punitive damages arising out of your use of, or inability to use, this website, even if advised of the possibility of such damages. The site is provided "AS IS" without warranties of any kind.',
  },
  {
    icon: Info,
    color: '#00f0ff',
    title: '6. Changes to these terms',
    body:
      'The admin may modify these Terms & Conditions at any time without prior notice. Continued use of the site after changes constitutes acceptance of the revised terms. Last updated: Feb 2026.',
  },
];

export default function TermsModal({ isOpen, onClose }) {
  useEffect(() => {
    if (!isOpen) return;
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-start sm:items-center justify-center p-4 pt-16 sm:pt-8 bg-black/85 backdrop-blur-md overflow-y-auto"
          onClick={onClose}
          data-testid="terms-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-3xl glass-card rounded-2xl border border-[#9d4edd]/30 overflow-hidden shadow-2xl flex flex-col"
            style={{ maxHeight: 'calc(100vh - 6rem)' }}
          >
            <div className="bg-slate-950/85 p-4 border-b border-slate-800 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2.5">
                <ScrollText className="w-4 h-4 text-[#9d4edd]" />
                <h2 className="font-display font-bold text-sm tracking-widest text-[#9d4edd] uppercase">
                  Terms &amp; Conditions
                </h2>
              </div>
              <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10 text-slate-400 hover:text-white transition-colors" data-testid="btn-close-terms">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto space-y-5">
              <p className="text-slate-400 text-xs font-light leading-relaxed">
                Please read these Terms &amp; Conditions carefully before registering for an account or interacting with content on this website (the &quot;Site&quot;). By creating an account, you agree to be bound by these terms.
              </p>
              {SECTIONS.map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.title} className="p-4 border border-slate-800 rounded-lg bg-slate-950/40">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: s.color }} />
                      <h3 className="font-display font-bold text-xs uppercase tracking-wide" style={{ color: s.color }}>{s.title}</h3>
                    </div>
                    <p className="text-slate-300 text-xs leading-relaxed font-light">{s.body}</p>
                  </div>
                );
              })}
              <div className="text-center font-mono text-[10px] text-slate-500 pt-2">
                By proceeding with registration you confirm that you have read, understood, and accept all of the above.
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
